import { Button, Empty, Flex, IconFont, Input, MobileModal, pointer, singleTextEllipsis, Text, tinyButtons } from '@byecode/ui'
import type { FilterOption, NumberRangeArray } from '@lighthouse/core'
import { AppUser, NumberRangeMode } from '@lighthouse/core'
import { useLanguageContext } from '@lighthouse/shared'
import type { DrawerProps } from '@mantine/core'
import { clone, findIndex } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'

import { CUSTOM_VALUE } from '../constants'
import { CustomDateFilter } from '../CustomDateFilter'

interface DateDrawerProps {
    opened: boolean
    isMultiple?: boolean
    leftIcon?: string
    options?: FilterOption[]
    value?: string[]
    customValue?: NumberRangeArray
    showCustom?: boolean
    title: string
    target: DrawerProps['target']
    onFinish: (val: string[], customValue?: NumberRangeArray) => void
    onClose: () => void
}
const SCxContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`

const SCxTagList = styled.div`
    overflow-y: auto;
    ${tinyButtons}
    width: 100%;
    border: 1px solid var(--color-gray-200);
    border-radius: 8px;
`

const SCxTagWrapper = styled.div`
    height: auto;
    overflow: hidden;
`

const SCxTagItem = styled.div<{ isShowBorder?: boolean }>`
    width: 100%;
    display: flex;
    /* height: 300px; */
    align-items: center;
    justify-content: space-between;
    height: 48px;
    padding: 16px;
    color: var(--color-black);
    border-bottom: 1px solid var(--color-gray-200);
    background: var(--color-white);

    &:last-child {
        border: none;
    }
`

const emptyArr: any[] = []
export const DateDrawer: React.FC<DateDrawerProps> = ({
    opened,
    leftIcon,
    isMultiple = false,
    options = emptyArr,
    title,
    value = emptyArr,
    customValue,
    showCustom,
    target,
    onClose,
    onFinish
}) => {
    const { convertTextByLanguage } = useLanguageContext() // 引入语言上下文

    const [customInputValue, setCustomInputValue] = useState(customValue)
    const [selectValue, setSelectValue] = useState(value)

    const showCustomInput = useMemo(
        () => (selectValue.includes(CUSTOM_VALUE) && options.find(option => option.value === CUSTOM_VALUE)) || showCustom,
        [options, selectValue, showCustom]
    )

    useUpdateEffect(() => {
        if (value !== selectValue) {
            setSelectValue(value)
        }
    }, [value])

    useUpdateEffect(() => {
        if (customValue !== customInputValue) {
            setCustomInputValue(customValue)
        }
    }, [customValue])

    const handleFinish = useCallback(() => {
        onFinish?.(selectValue, customInputValue)
        onClose()
    }, [onFinish, selectValue, customInputValue, onClose])

    const handleReset = useCallback(() => {
        onFinish?.([], undefined)
        setSelectValue([])
        setCustomInputValue(undefined)
        onClose()
    }, [onClose, onFinish])

    const handleSelectChange = useCallback(
        (val: string) => {
            const isActive = selectValue.includes(val)
            if (isMultiple) {
                const newValue = isActive ? selectValue.filter(v => v !== val) : [...selectValue, val]
                setSelectValue(newValue)
                return
            }
            const newValue = isActive ? [] : [val]
            setSelectValue(newValue)
            setCustomInputValue(undefined)
        },
        [isMultiple, selectValue]
    )

    return (
        <MobileModal
            leftSlot={!!leftIcon && <IconFont type={leftIcon} />}
            withCloseIcon={!isMultiple}
            data-ignore-click-away
            target={target}
            title={title} // 替换标题文本
            open={opened}
            styles={{
                modal: {
                    maxHeight: 600,
                    height: options.length > 0 ? '50%' : 'auto!important',
                    backgroundColor: 'var(--color-gray-50)'
                }
            }}
            onClose={onClose}
        >
            <SCxContainer>
                <Flex
                    direction="column"
                    style={{
                        flex: 1,
                        overflow: 'hidden',
                        gap: 12
                    }}
                >
                    {options.length > 0 && (
                        <Flex style={{ flex: 1, overflow: 'hidden' }}>
                            <div
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'stretch'
                                }}
                            >
                                <SCxTagList>
                                    <SCxTagWrapper>
                                        {options.map(item => {
                                            const active = selectValue.includes(item.value) || (item.value === CUSTOM_VALUE && showCustom)
                                            return (
                                                <SCxTagItem key={item.value} onClick={() => handleSelectChange(item.value)}>
                                                    <Text>{item.label}</Text> {/* 替换选项文本 */}
                                                    {active && <IconFont size={16} color="var(--color-app-main)" type="Tick" />}
                                                </SCxTagItem>
                                            )
                                        })}
                                    </SCxTagWrapper>
                                </SCxTagList>
                            </div>
                        </Flex>
                    )}
                    {showCustomInput && (
                        <CustomDateFilter
                            customValue={customInputValue}
                            isMobile
                            onCustomChange={setCustomInputValue}
                            style={{
                                backgroundColor: '#fff',
                                padding: 16,
                                height: 64,
                                borderRadius: 8,
                                border: '1px solid var(--color-gray-200)'
                            }}
                        />
                    )}
                    <Flex gap={12} style={{ padding: '10px 0' }}>
                        <Button size="lg" block onClick={handleReset}>
                            {convertTextByLanguage('clear')} {/* 替换“清除”文本 */}
                        </Button>
                        <Button size="lg" block type="primary" style={{ backgroundColor: 'var(--color-app-main)' }} onClick={handleFinish}>
                            {convertTextByLanguage('sure')}
                        </Button>
                    </Flex>
                </Flex>
            </SCxContainer>
        </MobileModal>
    )
}
